import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

// Cache implementation for messages
const messageCache = {
  // Structure to store cached results
  cache: {},
  
  // Default expiration time in milliseconds (30 seconds)
  defaultExpiration: 30 * 1000,
  
  // Get a cached result if available and not expired
  get: function(key) {
    const item = this.cache[key];
    if (!item) {
      return null;
    }
    
    // Check if cached item has expired
    if (Date.now() > item.expiry) {
      delete this.cache[key];
      return null;
    }
    
    return item.data;
  },
  
  // Store a result in cache with custom expiration
  set: function(key, data, expirationMs) {
    const expiry = Date.now() + (expirationMs || this.defaultExpiration);
    this.cache[key] = { data, expiry };
  },
  
  // Clear a specific cache entry
  clear: function(key) {
    delete this.cache[key];
  },
  
  // Clear all cache entries
  clearAll: function() {
    this.cache = {};
  },
  
  // Clear all cache entries matching a key prefix
  clearByPrefix: function(prefix) {
    for (const key in this.cache) {
      if (key.startsWith(prefix)) {
        delete this.cache[key];
      }
    }
  }
};

// Store timestamps of recent API calls to implement basic rate limiting
const requestTimestamps = {
  createMessage: [],
  likeMessage: [],
  shareMessage: []
};

// Function to check if we should throttle a request
const shouldThrottle = (endpoint, rateLimit = 5, timeWindow = 60000) => {
  const now = Date.now();
  
  // Remove timestamps older than the time window
  requestTimestamps[endpoint] = requestTimestamps[endpoint].filter(
    timestamp => now - timestamp < timeWindow
  );
  
  // If we have fewer requests than the rate limit, allow this one
  if (requestTimestamps[endpoint].length < rateLimit) {
    requestTimestamps[endpoint].push(now);
    return false;
  }
  
  // Otherwise, throttle
  return true;
};

const messagesService = {
  // Generate cache keys for different types of requests
  getCacheKey: function(type, params) {
    const paramsStr = Object.keys(params)
      .sort()
      .map(key => `${key}=${params[key]}`)
      .join('&');
    return `${type}:${paramsStr}`;
  },
  
  // Clear cache when new content is posted
  invalidateMessageCache: function(type = null) {
    if (type) {
      messageCache.clearByPrefix(`${type}:`);
    } else {
      messageCache.clearAll();
    }
  },

  // Clear cache entries with a specific prefix
  clearByPrefix: function(prefix) {
    messageCache.clearByPrefix(prefix);
  },

  getRecentMessages: async (page = 1, limit = 10, lastId = null, university = null) => {
    try {
      const params = { page, limit };
      if (lastId) params.last_id = lastId;
      if (university) params.university = university;
      
      // Generate cache key
      const cacheKey = messagesService.getCacheKey('recent', params);
      
      // Check cache first
      const cachedData = messageCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      const response = await axios.get(`${API_URL}/api/messages/recent`, { params });
      
      // Format response data to be consistent
      const formattedData = Array.isArray(response.data) ? response.data.map(msg => {
        // Handle MongoDB ObjectID format
        let id = msg.id;
        if (!id && msg._id) {
          id = typeof msg._id === 'string' ? msg._id : 
               (msg._id.$oid ? msg._id.$oid : 
               (typeof msg._id === 'object' && msg._id.toString ? msg._id.toString() : msg._id));
        }
        
        return {
          id: id,
          text: msg.text,
          user_id: msg.user_id,
          username: msg.username,
          timestamp: msg.timestamp,
          likes: msg.likes,
          comments: msg.comments,
          shares: msg.shares,
          liked: msg.liked || false,
          image: msg.image,
          hashtags: msg.hashtags || []
        };
      }) : [];
      
      // Cache the result - 60 seconds for first page, 2 minutes for other pages
      const cacheTime = page === 1 ? 60 * 1000 : 2 * 60 * 1000;
      messageCache.set(cacheKey, formattedData, cacheTime);
      
      return formattedData;
    } catch (error) {
      console.error('Error al obtener mensajes recientes:', error);
      throw error;
    }
  },
  
  getPopularMessages: async (period = 'all', page = 1, limit = 10, university = null) => {
    try {
      // Add detailed logging for debugging
      const params = { period, page, limit };
      if (university) params.university = university;
      
      // Generate cache key
      const cacheKey = messagesService.getCacheKey('popular', params);
      
      // Check cache first - longer cache time for popular content
      const cachedData = messageCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      const response = await axios.get(`${API_URL}/api/messages/popular`, {
        params
      });
      
      // Format response data to be consistent with other methods
      let formattedMessages = Array.isArray(response.data) ? response.data.map(msg => {
        // Handle MongoDB ObjectID format
        let id = msg.id;
        if (!id && msg._id) {
          id = typeof msg._id === 'string' ? msg._id : 
               (msg._id.$oid ? msg._id.$oid : 
               (typeof msg._id === 'object' && msg._id.toString ? msg._id.toString() : msg._id));
        }
        
        // Standardize the timestamp format
        let timestamp = msg.timestamp;
        if (timestamp) {
          // Ensure it's a string in ISO format
          if (typeof timestamp === 'object' && timestamp.toISOString) {
            timestamp = timestamp.toISOString();
          } else if (typeof timestamp !== 'string') {
            timestamp = new Date(timestamp).toISOString();
          }
        }
        
        return {
          id: id,
          text: msg.text,
          user_id: msg.user_id,
          username: msg.username,
          timestamp: timestamp,
          created_at: msg.created_at || timestamp,
          likes: msg.likes,
          comments: msg.comments,
          shares: msg.shares,
          liked: msg.liked || false,
          image: msg.image,
          hashtags: msg.hashtags || []
        };
      }) : [];
      
      // Implement a strict filtering for "day" period regardless of backend implementation
      if (period === 'day' && Array.isArray(formattedMessages)) {
        // Get start of today in user's timezone
        const now = new Date();
        
        const filteredMessages = formattedMessages.filter(msg => {
          if (!msg.timestamp) {
            return false;
          }
          
          try {
            // Parse the message timestamp
            const msgDate = new Date(msg.timestamp);
            
            // For reliable date comparison, create dates with only year, month, day components
            const msgLocalDate = new Date(
              msgDate.getFullYear(),
              msgDate.getMonth(),
              msgDate.getDate()
            );
            
            const nowLocalDate = new Date(
              now.getFullYear(),
              now.getMonth(),
              now.getDate()
            );
            
            // Compare only the date portion (ignoring time)
            const isSameDay = msgLocalDate.getTime() === nowLocalDate.getTime();
            
            // Only include messages from today
            return isSameDay;
          } catch (err) {
            console.error('Error parsing date:', err, msg.timestamp);
            return false;
          }
        });
        
        formattedMessages = filteredMessages;
      }
      
      // Cache the result with different expiration times based on period
      let cacheTime;
      switch(period) {
        case 'day':
          cacheTime = 60 * 1000; // 1 minute for daily popular
          break;
        case 'week':
          cacheTime = 5 * 60 * 1000; // 5 minutes for weekly popular
          break;
        case 'month':
          cacheTime = 10 * 60 * 1000; // 10 minutes for monthly popular
          break;
        case 'all':
          cacheTime = 15 * 60 * 1000; // 15 minutes for all-time popular
          break;
        default:
          cacheTime = 5 * 60 * 1000; // 5 minutes default
      }
      
      // Cache for less time on page 1 (most frequently accessed)
      if (page === 1) {
        cacheTime = Math.min(cacheTime, 2 * 60 * 1000); // Max 2 minutes for first page
      }
      
      messageCache.set(cacheKey, formattedMessages, cacheTime);
      
      return formattedMessages;
    } catch (error) {
      console.error('Error al obtener mensajes populares:', error);
      throw error;
    }
  },
  
  getMessagesByHashtag: async (hashtag, page = 1, limit = 10, university = null) => {
    try {
      const params = { page, limit };
      
      // Añadir el filtro de universidad si está disponible
      if (university) {
        params.university = university;
      }
      
      // Generar clave de caché
      const cacheKey = messagesService.getCacheKey('hashtag', { hashtag, ...params });
      
      // Verificar caché primero
      const cachedData = messageCache.get(cacheKey);
      if (cachedData) {
        return cachedData;
      }
      
      const response = await axios.get(`${API_URL}/api/messages/hashtag`, {
        params: { ...params, hashtag }
      });
      
      // Format response data to be consistent with other methods
      const formattedData = Array.isArray(response.data) ? response.data.map(msg => {
        // Handle MongoDB ObjectID format
        let id = msg.id;
        if (!id && msg._id) {
          id = typeof msg._id === 'string' ? msg._id : 
               (msg._id.$oid ? msg._id.$oid : 
               (typeof msg._id === 'object' && msg._id.toString ? msg._id.toString() : msg._id));
        }
        
        return {
          id: id,
          text: msg.text,
          user_id: msg.user_id,
          username: msg.username,
          timestamp: msg.timestamp,
          likes: msg.likes,
          comments: msg.comments,
          shares: msg.shares,
          liked: msg.liked || false,
          image: msg.image,
          hashtags: msg.hashtags || []
        };
      }) : [];
      
      // Guardar en caché por 2 minutos
      messageCache.set(cacheKey, formattedData, 2 * 60 * 1000);
      
      return formattedData;
    } catch (error) {
      console.error('Error al obtener mensajes por hashtag:', error);
      throw error;
    }
  },
  
  getMessage: async (id) => {
    try {
      const response = await axios.get(`${API_URL}/api/messages/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error al obtener mensaje:', error);
      throw error;
    }
  },
  
  likeMessage: async (messageId, action = 'like') => {
    try {
      // Basic frontend rate limiting (20 likes per minute - increased from 10)
      if (shouldThrottle('likeMessage', 20, 60000)) {
        throw new Error('Estás dando likes demasiado rápido. Por favor, espera un momento.');
      }
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación');
      }
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      const data = { action };
      const response = await axios.post(`${API_URL}/api/messages/${messageId}/like`, data, config);
      
      // Invalidate message cache for this user
      try {
        // Try to import usersService without creating circular dependencies
        const { default: usersService } = await import('./users');
        
        // If response contains the message owner's username, invalidate their profile too
        if (response.data && response.data.username) {
          usersService.handleMessageLiked(messageId, null, response.data.username);
        }
      } catch (err) {
        console.warn('Could not invalidate user cache:', err);
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al dar like:', error);
      
      // If it's our throttling error, return it directly
      if (error.message === 'Estás dando likes demasiado rápido. Por favor, espera un momento.') {
        throw error;
      }
      
      // For rate limit responses from the server
      if (error.response && error.response.status === 429) {
        throw new Error(error.response.data || 'Has alcanzado el límite de likes. Inténtalo más tarde.');
      }
      
      throw error;
    }
  },
  
  shareMessage: async (messageId) => {
    try {
      // Basic frontend rate limiting (10 shares per 5 minutes)
      if (shouldThrottle('shareMessage', 10, 5 * 60000)) {
        throw new Error('Estás compartiendo mensajes demasiado rápido. Por favor, espera un momento.');
      }
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación');
      }
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      };
      
      const response = await axios.post(`${API_URL}/api/messages/${messageId}/share`, {}, config);
      return response.data;
    } catch (error) {
      console.error('Error al compartir mensaje:', error);
      
      // If it's our throttling error, return it directly
      if (error.message === 'Estás compartiendo mensajes demasiado rápido. Por favor, espera un momento.') {
        throw error;
      }
      
      throw error;
    }
  },
  
  createMessage: async (formData) => {
    try {
      // Basic frontend rate limiting (10 messages per minute - increased from 5)
      if (shouldThrottle('createMessage', 10, 60000)) {
        throw new Error('Estás enviando mensajes demasiado rápido. Por favor, espera un momento.');
      }
      
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No se ha encontrado un token de autenticación');
      }
      
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };
      
      const response = await axios.post(`${API_URL}/api/messages`, formData, config);
      
      // Clear cache when a new message is created
      messagesService.invalidateMessageCache();
      
      // Invalidate the user's profile cache to update message counts
      try {
        const userString = localStorage.getItem('user');
        if (userString) {
          const user = JSON.parse(userString);
          
          // Try to import usersService without creating circular dependencies
          const { default: usersService } = await import('./users');
          
          // Update user's profile cache with new message count
          if (user && user.username) {
            usersService.handleMessageCreated(user.id, user.username);
          }
        }
      } catch (err) {
        console.warn('Could not invalidate user cache:', err);
      }
      
      // También invalidar el caché de hashtags si el mensaje contiene texto
      // ya que podría incluir nuevos hashtags
      const messageText = formData.get('text');
      if (messageText && typeof messageText === 'string' && messageText.includes('#')) {
        // Importar de forma dinámica para evitar dependencias circulares
        const { default: hashtagsService } = await import('./hashtags');
        
        // Obtener la universidad del mensaje para invalidar solo ese caché específico
        const university = formData.get('university');
        if (university) {
          hashtagsService.invalidateUniversityHashtags(university);
        } else {
          hashtagsService.invalidateHashtagsCache();
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error al crear mensaje:', error);
      
      // If it's our throttling error, return it directly
      if (error.message === 'Estás enviando mensajes demasiado rápido. Por favor, espera un momento.') {
        throw error;
      }
      
      // For rate limit responses from the server
      if (error.response && error.response.status === 429) {
        throw new Error(error.response.data || 'Has alcanzado el límite de mensajes. Inténtalo más tarde.');
      }
      
      throw error;
    }
  },
  
  checkLikeStatus: async (messageId) => {
    try {
      // First check SessionStorage
      try {
        const storageKey = `message_like_${messageId}`;
        const storedLikeStatus = sessionStorage.getItem(storageKey);
        
        if (storedLikeStatus !== null) {
          // Use stored status as source of truth
          const liked = storedLikeStatus === 'true';
          return { liked };
        }
      } catch (storageError) {
        console.warn('Error checking SessionStorage:', storageError);
      }

      // Generate a cache key for this like status check
      const cacheKey = `likeStatus:${messageId}`;
      
      // Check cache second
      const cachedData = messageCache.get(cacheKey);
      if (cachedData) {
        // Store in SessionStorage for next time
        try {
          const storageKey = `message_like_${messageId}`;
          sessionStorage.setItem(storageKey, cachedData.liked ? 'true' : 'false');
        } catch (storageError) {
          console.warn('Could not store in SessionStorage:', storageError);
        }
        
        return cachedData;
      }
      
      // Check if there's an ongoing request for this messageId to prevent duplicate calls
      if (messagesService.pendingLikeStatusChecks && messagesService.pendingLikeStatusChecks[messageId]) {
        return { liked: false, pending: true };
      }
      
      // Initialize pendingLikeStatusChecks if not exists
      if (!messagesService.pendingLikeStatusChecks) {
        messagesService.pendingLikeStatusChecks = {};
      }
      
      // Mark this request as pending
      messagesService.pendingLikeStatusChecks[messageId] = true;
      
      // Obtener el token de autenticación
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Verificar el ID del mensaje
      if (!messageId) {
        throw new Error('Message ID is required');
      }
      
      // Configurar los headers
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      // Realizar la solicitud para verificar el estado
      const response = await axios.get(`${API_URL}/api/messages/${messageId}/like/status`, config);
      
      // Cache the result for 1 minute
      messageCache.set(cacheKey, response.data, 60 * 1000);
      
      // Store in SessionStorage too
      try {
        const storageKey = `message_like_${messageId}`;
        sessionStorage.setItem(storageKey, response.data.liked ? 'true' : 'false');
      } catch (storageError) {
        console.warn('Could not store in SessionStorage:', storageError);
      }
      
      // Remove from pending requests
      delete messagesService.pendingLikeStatusChecks[messageId];
      
      return response.data;
    } catch (error) {
      console.error('Error checking like status:', error);
      
      // Remove from pending requests even on error
      if (messagesService.pendingLikeStatusChecks && messagesService.pendingLikeStatusChecks[messageId]) {
        delete messagesService.pendingLikeStatusChecks[messageId];
      }
      
      // Return default value
      return { liked: false };
    }
  },
  
  // Add a property to track pending requests
  pendingLikeStatusChecks: {},
  
  // Clear cache for a specific hashtag
  invalidateHashtagCache: function(hashtag) {
    if (hashtag) {
      // Usar una expresión sencilla para limpiar todas las entradas que incluyan este hashtag
      for (const key in messageCache.cache) {
        if (key.includes(`hashtag=`) && key.includes(hashtag)) {
          messageCache.clear(key);
        }
      }
    }
  },
  
  // Add batch processing for like status checks
  batchLikeStatusQueue: [],
  batchProcessing: false,
  batchTimeout: null,

  processBatchLikeStatusChecks: async function() {
    if (this.batchProcessing || this.batchLikeStatusQueue.length === 0) {
      return;
    }
    
    this.batchProcessing = true;
    
    try {
      // Take the first 10 items max for processing
      const batchItems = this.batchLikeStatusQueue.splice(0, 10);
      const messageIds = batchItems.map(item => item.messageId);
      
      // Get the token
      const token = localStorage.getItem('authToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Set up headers
      const config = {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };
      
      // FALLBACK METHOD: Make individual requests with rate limiting
      // This is used if the backend doesn't support batch requests
      const results = [];
      
      // Process messages sequentially to avoid overwhelming the server
      for (let i = 0; i < messageIds.length; i++) {
        const messageId = messageIds[i];
        
        try {
          // Check if it's already in the cache (might have been added by another process)
          const cacheKey = `likeStatus:${messageId}`;
          const cachedData = messageCache.get(cacheKey);
          
          if (cachedData) {
            results.push({ messageId, ...cachedData });
            
            // Store in SessionStorage for consistency
            try {
              const storageKey = `message_like_${messageId}`;
              sessionStorage.setItem(storageKey, cachedData.liked ? 'true' : 'false');
            } catch (storageError) {
              console.warn('Could not store in SessionStorage:', storageError);
            }
            
            continue;
          }
          
          // Wait a bit between requests to avoid rate limiting
          if (i > 0) {
            await new Promise(resolve => setTimeout(resolve, 150));
          }
          
          // Make the individual request
          const response = await axios.get(`${API_URL}/api/messages/${messageId}/like/status`, config);
          
          // Add to results
          results.push({ messageId, ...response.data });
          
          // Cache the result for this message
          messageCache.set(cacheKey, response.data, 60 * 1000);
          
          // Store in SessionStorage for next time
          try {
            const storageKey = `message_like_${messageId}`;
            sessionStorage.setItem(storageKey, response.data.liked ? 'true' : 'false');
          } catch (storageError) {
            console.warn('Could not store in SessionStorage:', storageError);
          }
          
        } catch (error) {
          console.warn(`Error checking like status for message ${messageId}:`, error);
          // Add default result on error
          results.push({ messageId, liked: false });
        }
      }
      
      // Process results and resolve individual promises
      results.forEach(result => {
        const { messageId, liked } = result;
        
        // Find and resolve the corresponding item's resolve function
        const item = batchItems.find(i => i.messageId === messageId);
        if (item && item.resolve) {
          item.resolve({ liked });
        }
      });
    } catch (error) {
      console.error('Error processing batch like status checks:', error);
      
      // Resolve all pending promises with a default value
      this.batchLikeStatusQueue.forEach(item => {
        if (item.resolve) {
          item.resolve({ liked: false });
        }
      });
      
      // Clear the queue on error
      this.batchLikeStatusQueue = [];
    } finally {
      this.batchProcessing = false;
      
      // Process next batch if there are more items
      if (this.batchLikeStatusQueue.length > 0) {
        setTimeout(() => this.processBatchLikeStatusChecks(), 200);
      }
    }
  },

  checkLikeStatusBatch: function(messageId) {
    // First check SessionStorage
    try {
      const storageKey = `message_like_${messageId}`;
      const storedLikeStatus = sessionStorage.getItem(storageKey);
      
      if (storedLikeStatus !== null) {
        // Use stored status as source of truth
        const liked = storedLikeStatus === 'true';
        return Promise.resolve({ liked });
      }
    } catch (storageError) {
      console.warn('Error checking SessionStorage:', storageError);
    }
    
    // Generate a cache key for this like status check
    const cacheKey = `likeStatus:${messageId}`;
    
    // Check cache second
    const cachedData = messageCache.get(cacheKey);
    if (cachedData) {
      // Store in SessionStorage for consistency
      try {
        const storageKey = `message_like_${messageId}`;
        sessionStorage.setItem(storageKey, cachedData.liked ? 'true' : 'false');
      } catch (storageError) {
        console.warn('Could not store in SessionStorage:', storageError);
      }
      
      return Promise.resolve(cachedData);
    }
    
    // Create a new promise that will be resolved when the batch is processed
    return new Promise((resolve) => {
      // Add to the queue
      this.batchLikeStatusQueue.push({ messageId, resolve });
      
      // Clear any existing timeout
      if (this.batchTimeout) {
        clearTimeout(this.batchTimeout);
      }
      
      // Set a new timeout to process the queue after a short delay
      // This allows multiple requests to be batched together
      this.batchTimeout = setTimeout(() => {
        this.processBatchLikeStatusChecks();
      }, 50);
    });
  }
};

export default messagesService;